'use client';

import { Svg, Text } from '@/components';
import {
  CrossCircleIcon,
  LocationPinIcon,
  SuccessTickCircleIcon,
} from '@/icons';
import { GURUGRAM_STORE_CODE } from '@/constants';
import { Zap } from 'lucide-react';
import { FC } from 'react';
import { useLocation } from '@/context/locationContext';
import StorePickupIcon from '@/icons/StorePickupIcon';
import { storage } from '@/utils/storage';

type TooltipToastProps = {
  message: string;
  type?: 'success' | 'failure';
  deliveryType?: 'home' | 'pickup';
};

const TooltipToast: FC<TooltipToastProps> = ({
  message,
  type = 'success',
  deliveryType,
}) => {
  const { headerLocationTitle, expressStandard, expressStandardLoading } =
    useLocation();
  const isGurgaon =
    deliveryType === 'home' &&
    storage.get('store_code') === GURUGRAM_STORE_CODE;

  if (expressStandardLoading) {
    return null;
  }

  return (
    <>
      {!deliveryType ? (
        <div className='tooltip-toast flex items-center justify-center gap-x-2 rounded-lg'>
          {type !== 'success' ? (
            <Svg
              fill='none'
              width='80'
              height='80'
              viewBox='0 0 80 80'
              className='mx-auto h-8 w-8 shrink-0 grow stroke-none'>
              <CrossCircleIcon />
            </Svg>
          ) : (
            <Svg
              width='45'
              height='44'
              viewBox='0 0 45 44'
              className='h-8 w-8 shrink-0 grow stroke-none stroke-0'>
              <SuccessTickCircleIcon />
            </Svg>
          )}
          <Text className='max-w-sm px-0 text-sm md:text-base'>{message}</Text>
        </div>
      ) : isGurgaon || expressStandard ? (
        <div className='tooltip-toast-location items-left flex flex-col items-start justify-center gap-x-2 rounded-lg bg-[#FDFBFA] px-3 py-2.5 top-0 right-[30%]'>
          <div className='items-left flex items-start justify-center gap-x-2 text-left'>
            <Svg className='h-5 w-5 shrink-0 stroke-[#A0383F] md:h-6 md:w-6'>
              <LocationPinIcon />
            </Svg>
            <Text className='px-0 text-xs font-semibold md:text-sm'>
              {headerLocationTitle}
            </Text>
          </div>
          <div className='pt-1 flex flex-wrap items-baseline gap-x-1 text-left'>
            <span className='text-[#4A4A4A] text-xs md:text-sm'>
              You&apos;re in our{' '}
            </span>
            <Zap
              size={12}
              className='inline shrink-0 align-baseline stroke-fig'
            />
            <span className='text-fig font-light text-xs uppercase md:text-sm'>
              EXPRESS DELIVERY
            </span>
            <span className='text-[#4A4A4A] text-xs md:text-sm'>
              {' '}
              zone! Get your order in just{' '}
            </span>
            <span className='font-bold text-fig text-xs uppercase md:text-sm'>
              2 HOURS
            </span>
          </div>
        </div>
      ) : (
        <div className='tooltip-toast-location items-left flex flex-col items-start justify-center gap-x-2 rounded-lg bg-white top-0 right-[30%]'>
          <div className='items-left flex items-start justify-center text-left'>
            {deliveryType === 'home' ? (
              <Svg className='h-5 w-5 stroke-[#A0383F] md:h-6 md:w-6'>
                <LocationPinIcon />
              </Svg>
            ) : (
              <Svg
                fill='#A0383F'
                className='h-5 w-5 stroke-fig stroke-[0.3] md:h-6 md:w-6'>
                <StorePickupIcon />
              </Svg>
            )}
            <Text className='px-0 text-xs font-semibold md:text-sm'>
              {headerLocationTitle}
            </Text>
          </div>
          <Text className='max-w-sm px-0 text-left text-xs md:text-base'>
            {message}
          </Text>
        </div>
      )}
    </>
  );
};

export default TooltipToast;
